import IdentificationTypesSelect from '@/components/common/input/identification-types-select/IdentificationTypesSelect';
import CalendarModal from '@/components/modals/calendar-modal/CalendarModal';
import { Button } from '@/components/ui/button';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  SelectItem,
  SelectTrigger,
  Select,
  SelectContent,
  SelectValue
} from '@/components/ui/select';
import useMixPanel from '@/hooks/useMixPanel';
import { useRootStore } from '@/providers/root-store-provider';
import { TUserData } from '@/services/api/user-api';
import { parentsVEP } from '@/utils/utils';
import { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

const UserForm: React.FC<{
  form: UseFormReturn<any, any, undefined>;
  user: TUserData | undefined;
  onSave: () => Promise<void>;
}> = ({ form, user, onSave }) => {
  const Mixpanel = useMixPanel();
  const { t } = useTranslation(['newBooking', 'common']);

  const cleanConsumers = useRootStore((store) => store.cleanConsumers);
  const loadStepServicesData = useRootStore(
    (store) => store.loadStepServicesData
  );

  const handleValidate = () => {
    if (form.formState.errors) {
      const firstParentKey = Object.keys(form.formState.errors)[0];
      const firstErrorField = Object.keys(
        form.formState.errors[firstParentKey] ?? {}
      )[0];
      if (firstErrorField) {
        form.setFocus(firstErrorField as any);
        document
          .querySelector(`[name="user.${firstErrorField}"]`)
          ?.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          });
      }
    }
    onSave();
    const data = form.getValues();
    cleanConsumers('user');
    loadStepServicesData(data.user);
  };

  return (
    <div
      className="border border-primary p-6 rounded-md flex flex-col gap-8"
      key={`participant-participant`}
    >
      <FormField
        control={form.control}
        name={`user.firstName`}
        render={({ field }) => {
          const disabled = !!user?.firstName;
          return (
            <FormItem>
              <FormLabel>{t('stepParticipants.form.firstName')}</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  onClick={() =>
                    Mixpanel.track('press user name input', {
                      screen: 'New Booking',
                      organization: 'Viento en popa'
                    })
                  }
                  readOnly={disabled}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          );
        }}
      />
      <FormField
        control={form.control}
        name={`user.lastName`}
        render={({ field }) => {
          const disabled = !!user?.firstName;
          return (
            <FormItem>
              <FormLabel>{t('stepParticipants.form.lastName')}</FormLabel>
              <FormControl>
                <Input
                  onClick={() =>
                    Mixpanel.track('press lastName input', {
                      screen: 'New Booking',
                      organization: 'Viento en popa'
                    })
                  }
                  {...field}
                  readOnly={disabled}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          );
        }}
      />
      <FormField
        control={form.control}
        name="user.identification.type"
        render={({ field }) => {
          const disabled = !!user?.identification?.type;
          return (
            <FormItem>
              <IdentificationTypesSelect
                disabled={disabled}
                defaultValue={field?.value}
                onValueChange={(value) => {
                  Mixpanel.track('press user id input', {
                    screen: 'New Booking',
                    organization: 'Viento en popa'
                  });
                  field.onChange(value);
                }}
                field={field}
              />
              <FormMessage />
            </FormItem>
          );
        }}
      />
      <FormField
        control={form.control}
        name="user.identification.number"
        render={({ field }) => {
          const disabled = !!user?.identification?.number;
          return (
            <FormItem>
              <FormLabel>
                {t('stepParticipants.form.identificationNumber')}
              </FormLabel>
              <FormControl>
                <Input
                  onClick={() =>
                    Mixpanel.track('press participants id number input', {
                      screen: 'New Booking',
                      organization: 'Viento en popa'
                    })
                  }
                  readOnly={disabled}
                  placeholder={t('stepParticipants.form.id')}
                  {...field}
                  value={field?.value || ''}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          );
        }}
      />
      <FormField
        control={form.control}
        name="user.location"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('summaryModal.fields.address')}</FormLabel>
            <FormControl>
              <Input
                onClick={() =>
                  Mixpanel.track('press age input', {
                    screen: 'New Booking',
                    organization: 'Viento en popa'
                  })
                }
                {...field}
                value={field?.value || ''}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name={`user.birthdate`}
        render={({ field }) => {
          const disabled = !!user?.birthdate;
          return (
            <FormItem className="flex flex-col ">
              <FormLabel>{t('stepParticipants.form.birth')}</FormLabel>
              <CalendarModal
                value={field.value}
                placeholder={t('stepParticipants.form.birth')}
                onAccept={(value) => {
                  field.onChange(value);
                  Mixpanel.track('select date from birthday calendar ', {
                    screen: 'New Booking',
                    organization: 'Viento en popa'
                  });
                }}
                disabledInputLabel={disabled}
                onClickInputLabel={() =>
                  Mixpanel.track('press birthday date button ', {
                    screen: 'New Booking',
                    organization: 'Viento en popa'
                  })
                }
                initialFocus
                dropDownPicker
              />
              <FormMessage />
            </FormItem>
          );
        }}
      />

      <FormField
        control={form.control}
        name="user.insurance"
        render={({ field }) => {
          return (
            <FormItem>
              <FormLabel>{t('insurance')}</FormLabel>
              <FormControl>
                <Input
                  onClick={() =>
                    Mixpanel.track('press insurance input', {
                      screen: 'New Booking',
                      organization: 'Viento en popa'
                    })
                  }
                  placeholder={'OSDE, Swiss Medical, etc'}
                  {...field}
                  value={field?.value || ''}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          );
        }}
      />
      <FormField
        control={form.control}
        name="user.holidayDate"
        render={({ field }) => {
          const valueNoOrUndefined = field.value === false ? 'no' : undefined;
          return (
            <FormItem>
              <div className="flex flex-col gap-2">
                <FormLabel>{t('holidayDate')}</FormLabel>
                <FormDescription>{t('holidayDescription')}</FormDescription>
                <FormControl>
                  <RadioGroup
                    onValueChange={(value) => {
                      const valueNoOrUndefined =
                        value === 'no' ? false : undefined;
                      field.onChange(
                        value === 'yes' ? true : valueNoOrUndefined
                      );
                    }}
                    value={field.value === true ? 'yes' : valueNoOrUndefined}
                    className="flex space-x-4 mt-1 items-center"
                  >
                    <FormItem className="flex items-center space-x-1 space-y-0">
                      <FormControl>
                        <RadioGroupItem value="yes" />
                      </FormControl>
                      <FormLabel className="font-normal">{t('yes')}</FormLabel>
                    </FormItem>

                    <FormItem className="flex items-center space-x-1 space-y-0">
                      <FormControl>
                        <RadioGroupItem value="no" />
                      </FormControl>
                      <FormLabel className="font-normal">{t('no')}</FormLabel>
                    </FormItem>
                  </RadioGroup>
                </FormControl>
                {field.value === true && (
                  <FormField
                    control={form.control}
                    name="user.holidayDateDetails"
                    render={({ field: detailsField }) => (
                      <FormItem className="mt-2">
                        <FormControl>
                          <Input
                            placeholder="Del 12/01 al 18/01"
                            {...detailsField}
                            onClick={() =>
                              Mixpanel.track('press holiday date input', {
                                screen: 'New Booking',
                                organization: 'Viento en popa'
                              })
                            }
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
              </div>
              <FormMessage />
            </FormItem>
          );
        }}
      />
      <FormField
        control={form.control}
        name={'user.isExtension'}
        render={({ field }) => {
          const valueYesOrNo = field.value ? 'yes' : 'no';
          const defaultValue =
            field.value === undefined ? undefined : valueYesOrNo;
          return (
            <FormItem>
              <div className="flex flex-row items-end gap-5">
                <FormLabel>{t('isExtension')}</FormLabel>
                <FormControl>
                  <RadioGroup
                    onValueChange={(value) => field.onChange(value === 'yes')}
                    defaultValue={defaultValue}
                    className="flex space-x-2 mt-0 items-center"
                  >
                    <FormItem className="flex items-center space-x-1 space-y-0">
                      <FormControl>
                        <RadioGroupItem value="yes" />
                      </FormControl>
                      <FormLabel className="font-normal">{t('yes')}</FormLabel>
                    </FormItem>
                    <FormItem className="flex items-center space-x-1 space-y-0">
                      <FormControl>
                        <RadioGroupItem value="no" />
                      </FormControl>
                      <FormLabel className="font-normal">{t('no')}</FormLabel>
                    </FormItem>
                  </RadioGroup>
                </FormControl>
              </div>
            </FormItem>
          );
        }}
      />
      <FormField
        control={form.control}
        name={'user.isEmployeePartner'}
        render={({ field }) => {
          return (
            <FormItem>
              <div className="flex flex-row items-end gap-5">
                <FormLabel>{t('isEmployeePartner')}</FormLabel>
                <FormControl>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue
                          placeholder={t('isEmployeePartnerPlaceholder')}
                        />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {parentsVEP.map((parent) => (
                        <SelectItem key={parent} value={parent}>
                          {parent}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormControl>
              </div>
            </FormItem>
          );
        }}
      />
      <FormField
        control={form.control}
        name={'user.isPartner'}
        render={({ field }) => {
          const valueYesOrNo = field.value ? 'yes' : 'no';
          const defaultValue =
            field.value === undefined ? undefined : valueYesOrNo;
          return (
            <FormItem>
              <div className="flex flex-row items-end gap-5">
                <FormLabel>{t('isPartner')}</FormLabel>
                <FormControl>
                  <RadioGroup
                    onValueChange={(value) => field.onChange(value === 'yes')}
                    defaultValue={defaultValue}
                    className="flex space-x-2 mt-0 items-center"
                  >
                    <FormItem className="flex items-center space-x-1 space-y-0">
                      <FormControl>
                        <RadioGroupItem value="yes" />
                      </FormControl>
                      <FormLabel className="font-normal">{t('yes')}</FormLabel>
                    </FormItem>
                    <FormItem className="flex items-center space-x-1 space-y-0">
                      <FormControl>
                        <RadioGroupItem value="no" />
                      </FormControl>
                      <FormLabel className="font-normal">{t('no')}</FormLabel>
                    </FormItem>
                  </RadioGroup>
                </FormControl>
              </div>
            </FormItem>
          );
        }}
      />
      <Button type="button" onClick={handleValidate}>
        {t('common:validate')}
      </Button>
    </div>
  );
};

export default UserForm;
